import styled from 'styled-components';
import Label from '../Label';
import { TypeCaption } from '../Typography';
import { mqLargeMin, mqMediumMin } from 'styled/tools/media-queries';

export const CardFrame = styled.div<{
  $color: 'seedgreen' | 'snowwhite';
  $active: boolean;
}>`
  // the visible background is :before, which bleeds past the frame and rests scaled down
  --bg-bleed: 40px;
  --bg-rest-scale: 0.9;
  background-color: transparent;
  border-radius: var(--radius-lg);
  padding: var(--spacing-56) var(--spacing-16) var(--spacing-16)
    var(--spacing-16);
  @media ${mqLargeMin} {
    --bg-bleed: 50px;
    padding: var(--spacing-40);
  }
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  color: var(--fill-brand);

  & img {
    aspect-ratio: 5 / 6;
    height: auto;
    transform: scale(1);
    transition: transform 0.3s linear;
  }

  & video {
    transform: scale(1);
    transition: transform 0.3s linear;
  }

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scaleY(var(--bg-rest-scale));
    width: 100%;
    height: calc(100% + var(--bg-bleed));
    transform-origin: center;
    transition: transform 0.3s;
    background-color: rgba(87, 94, 85, 0.08);
    background-color: ${({ $color }) =>
      $color === 'snowwhite'
        ? 'rgba(229, 240, 226, 0.08)'
        : 'var(--fill-brand-subtle)'};
    border-radius: var(--radius-lg);
  }

  &.in-view {
    position: relative;

    &:before {
      transform: translate(-50%, -50%) scaleY(1);
    }

    & img {
      transform: scale(1.25);
    }

    & video {
      transform: scale(1.25);
    }

    & > span {
      top: calc(var(--spacing-16) - var(--bg-bleed) / 2);
    }

    & > .block-top {
      transform: translateY(-20px);
    }

    & > .block-bottom {
      transform: translateY(20px);
    }
  }

  &:hover .video-overlay {
    opacity: 1 !important;
  }

  // design wants the buttons to behave as if hovered when the card is active
  ${({ $active, $color }) =>
    $active &&
    `
    button {
      background: ${
        $color === 'seedgreen' ? 'var(--fill-primary)' : 'var(--fill-brand)'
      } !important;
      color: ${
        $color === 'seedgreen' ? 'var(--fill-brand)' : 'var(--fill-primary)'
      } !important;
    }
  `}
`;

export const CardBlock = styled.div<{
  hoverColor: string;
  stackSize?: 'small' | 'xSmall';
}>`
  z-index: 1;
  text-align: center;
  transition: transform 0.3s;
  & > * + * {
    margin-top: ${({ stackSize }) =>
      stackSize === 'xSmall' ? 'var(--spacing-8)' : 'var(--spacing-16)'};
  }
  p,
  span {
    *:hover & {
      color: var(${({ hoverColor }) => `--text-${hoverColor}`});
    }
  }
`;

export const CardPills = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-16);
`;

export const CardLabel = styled(Label)`
  position: absolute;
  top: calc(
    (100% - var(--bg-rest-scale) * (100% + var(--bg-bleed))) / 2 +
      var(--spacing-16)
  );
  left: var(--spacing-16);
  transition: all 0.3s;
`;

export const CardCaption = styled(TypeCaption)`
  opacity: 0.7;
  // i have to override this one prop from typecaption
  @media ${mqMediumMin} {
    --fz: var(--font-size-300);
    --lh: 8px;
  }
`;

export const MediaContainer = styled.div<{ $posterUrl?: string }>`
  width: 157px;
  height: 157px;
  @media ${mqLargeMin} {
    width: 264px;
    height: 264px;
  }
  z-index: 1;
  display: flex;
  justify-content: center;

  // prevents a rough transition from poster to video
  & video {
    background-image: ${({ $posterUrl }) =>
      $posterUrl ? `url(${$posterUrl})` : 'none'};
    background-size: cover;
    background-position: center;
  }
`;
